import TrackingDataService from "@/services/TrackingDataService"
import { APP_SESSION_STORAGE_KEY, UserActionType } from "@/static/UserAction"
import { isNovaLoginSession, markNovaLoginSession } from "@/utils/userActionTracking"
import type { ICreateUserAction } from "@/types/Tracking.types"
import { defineStore } from "pinia"
import { useEstablishmentStore } from "./establishment"
import { useAuthenticationStore } from "./authentication"

export const useTrackingStore = defineStore("tracking", {
  actions: {
    trackUserAction(action: ICreateUserAction) {
      const authStore = useAuthenticationStore()
      if (!authStore.user) {
        return
      }

      TrackingDataService.createUserAction(action).catch(() => {
        // Le tracking ne doit pas impacter l'expérience utilisateur
      })
    },

    trackAppSession() {
      const authStore = useAuthenticationStore()
      if (!authStore.user?.email) {
        return
      }

      if (isNovaLoginSession()) {
        markNovaLoginSession()
        return
      }

      if (sessionStorage.getItem(APP_SESSION_STORAGE_KEY)) {
        return
      }

      sessionStorage.setItem(APP_SESSION_STORAGE_KEY, "1")

      const establishmentStore = useEstablishmentStore()
      const establishmentId = establishmentStore.currentEstablishment?.id ?? null

      this.trackUserAction({
        action_type: UserActionType.APP_SESSION,
        establishment_id: establishmentId,
      })
    },

    markAppSessionTracked() {
      sessionStorage.setItem(APP_SESSION_STORAGE_KEY, "1")
    },

    skipAppSessionForNovaLogin() {
      markNovaLoginSession()
    },

    trackDataSync(establishmentId: number) {
      this.trackUserAction({
        action_type: UserActionType.DATA_SYNC,
        establishment_id: establishmentId,
      })
    },

    trackDataUpdate(establishmentId: number, section: string) {
      this.trackUserAction({
        action_type: UserActionType.DATA_UPDATE,
        establishment_id: establishmentId,
        metadata: { section },
      })
    },

    trackReviewResponse(
      establishmentId: number,
      metadata: {
        review_id: number
        review_external_id?: string | null
        ai_generated: boolean
        ai_modified: boolean
        ai_accepted: boolean
      }
    ) {
      this.trackUserAction({
        action_type: UserActionType.REVIEW_RESPONSE,
        establishment_id: establishmentId,
        metadata,
      })
    },

    trackDemoCalendarInteract(establishmentId: number) {
      this.trackUserAction({
        action_type: UserActionType.DEMO_CALENDAR_INTERACT,
        establishment_id: establishmentId,
      })
    },

    trackDemoReviewSelect(
      establishmentId: number,
      metadata: {
        review_id: number
        review_external_id?: string | null
        tab: "unreplied" | "replied"
      }
    ) {
      this.trackUserAction({
        action_type: UserActionType.DEMO_REVIEW_SELECT,
        establishment_id: establishmentId,
        metadata,
      })
    },

    trackDemoReviewAiGenerate(
      establishmentId: number,
      metadata: {
        review_id: number
        review_external_id?: string | null
      }
    ) {
      this.trackUserAction({
        action_type: UserActionType.DEMO_REVIEW_AI_GENERATE,
        establishment_id: establishmentId,
        metadata,
      })
    },

    trackDemoReviewPublish(
      establishmentId: number,
      metadata: {
        review_id: number
        review_external_id?: string | null
        ai_generated: boolean
        ai_modified: boolean
      }
    ) {
      this.trackUserAction({
        action_type: UserActionType.DEMO_REVIEW_PUBLISH,
        establishment_id: establishmentId,
        metadata,
      })
    },

    trackDemoRequest(
      establishmentId: number,
      metadata: {
        source: "calendar" | "reviews"
        linked_to_sale: boolean
      }
    ) {
      this.trackUserAction({
        action_type: UserActionType.DEMO_REQUEST,
        establishment_id: establishmentId,
        metadata,
      })
    },

    trackDemoSchedule(
      establishmentId: number,
      metadata: {
        source: "publish_modal"
      }
    ) {
      this.trackUserAction({
        action_type: UserActionType.DEMO_SCHEDULE,
        establishment_id: establishmentId,
        metadata,
      })
    },

    trackDemoActivateVisibility(
      establishmentId: number,
      metadata: {
        source: "calendar" | "reviews"
      }
    ) {
      this.trackUserAction({
        action_type: UserActionType.DEMO_ACTIVATE_VISIBILITY,
        establishment_id: establishmentId,
        metadata,
      })
    },

    trackDemoOuverture(establishmentId: number) {
      this.trackUserAction({
        action_type: UserActionType.DEMO_OUVERTURE,
        establishment_id: establishmentId,
      })
    },

    trackCalendrierOuverture(establishmentId: number) {
      this.trackUserAction({
        action_type: UserActionType.CALENDRIER_OUVERTURE,
        establishment_id: establishmentId,
      })
    },

    trackKbConsultation(establishmentId: number) {
      this.trackUserAction({
        action_type: UserActionType.KB_CONSULTATION,
        establishment_id: establishmentId,
      })
    },
  },
})
